import type { Case } from '../types';
import type { CSVExportData } from './csvExport';

/**
 * 案件データヘルパー関数の型定義
 */
export interface CaseHelpers {
  getBillingPartnerName: (id: string | null | undefined) => string;
  getPickupPartnerName: (id: string | null | undefined) => string;
  getDeliveryPartnerName: (id: string | null | undefined) => string;
  getVehicleName: (id: string | null | undefined) => string;
  getTransporterName: (id: string | null | undefined) => string;
}

/**
 * CSV行データの型定義
 *
 * 案件一覧のCSV出力で使用される各行のデータ構造を定義します。
 * 全9項目（日付、依頼元、引取地、納車地、車名、車台番号、料金、ドライバー名、備考）を含みます。
 *
 * @remarks
 * この型はTypeScriptの型安全性を向上させ、IDE自動補完の精度を高めます。
 */
export type CsvRow = [
  pickupDate: string,
  billingPartner: string,
  pickupPartner: string,
  deliveryPartner: string,
  vehicleName: string,
  chassisNumber: string,
  price: number,
  transporterName: string,
  remarks: string,
];

/**
 * CSV出力データの品質検証結果
 */
export interface CsvDataQuality {
  /** 不完全なデータを持つ案件の数 */
  incompleteCount: number;
  /** 検出された問題のリスト */
  issues: string[];
  /** 総案件数 */
  totalCount: number;
}

/**
 * 案件一覧データをCSV出力用データに変換する
 *
 * 全9項目（日付、依頼元、引取地、納車地、車名、車台番号、料金、ドライバー名、備考）を
 * CSV形式に変換します。null/undefinedの値は適切なデフォルト値（空文字列または0）に変換されます。
 *
 * @param cases - 案件データ配列。空配列の場合はヘッダーのみ出力されます。
 * @param helpers - 案件データ変換用ヘルパー関数。ID→名前変換を行います。
 * @returns CSV出力用データ（ヘッダー、行データ、ファイル名）
 *
 * @example
 * ```typescript
 * const csvData = convertCasesToCsvData(cases, {
 *   getBillingPartnerName: (id) => partnerMap.get(id)?.name || '',
 *   getPickupPartnerName: (id) => partnerMap.get(id)?.name || '',
 *   getDeliveryPartnerName: (id) => partnerMap.get(id)?.name || '',
 *   getVehicleName: (id) => vehicleMap.get(id)?.name || '',
 *   getTransporterName: (id) => transporterMap.get(id)?.name || '',
 * });
 * exportToCSV(csvData);
 * ```
 */
/**
 * CSV出力前にデータ品質を検証する
 *
 * 「不明」や空データを検出し、ユーザーに警告を促すための情報を返します。
 *
 * @param cases - 検証対象の案件データ配列
 * @param helpers - 案件データ変換用ヘルパー関数
 * @returns データ品質検証結果
 *
 * @example
 * ```typescript
 * const quality = validateCsvDataQuality(cases, helpers);
 * if (quality.incompleteCount > 0) {
 *   console.warn(`${quality.incompleteCount}件の不完全なデータが検出されました`);
 *   quality.issues.forEach(issue => console.warn(issue));
 * }
 * ```
 */
export const validateCsvDataQuality = (cases: Case[], helpers: CaseHelpers): CsvDataQuality => {
  const issues: string[] = [];
  let incompleteCount = 0;

  cases.forEach((caseData, index) => {
    const rowNumber = index + 1;
    const caseProblems: string[] = [];

    // 依頼元チェック
    const billingName = helpers.getBillingPartnerName(caseData.billingPartnerId);
    if (billingName === '不明' || billingName === '' || billingName === '読み込み中...') {
      caseProblems.push('依頼元');
    }

    // 引取地チェック
    const pickupName = helpers.getPickupPartnerName(caseData.pickupPartnerId);
    if (pickupName === '不明' || pickupName === '' || pickupName === '読み込み中...') {
      caseProblems.push('引取地');
    }

    // 納車地チェック
    const deliveryName = helpers.getDeliveryPartnerName(caseData.deliveryPartnerId);
    if (deliveryName === '不明' || deliveryName === '' || deliveryName === '読み込み中...') {
      caseProblems.push('納車地');
    }

    // 車名チェック
    const vehicleName = helpers.getVehicleName(caseData.vehicleModelId);
    if (
      vehicleName === '車両名不明' ||
      vehicleName === '不明' ||
      vehicleName === '' ||
      vehicleName === '読み込み中...'
    ) {
      caseProblems.push('車名');
    }

    // 問題があれば記録
    if (caseProblems.length > 0) {
      incompleteCount++;
      const dateInfo = caseData.pickupDate || '日付不明';
      issues.push(`${rowNumber}行目（${dateInfo}）: ${caseProblems.join('、')}が不明`);
    }
  });

  return {
    incompleteCount,
    issues,
    totalCount: cases.length,
  };
};

export const convertCasesToCsvData = (cases: Case[], helpers: CaseHelpers): CSVExportData => {
  // CSVヘッダー（全9項目）
  const headers = [
    '日付',
    '依頼元',
    '引取地',
    '納車地',
    '車名',
    '車台番号',
    '料金',
    'ドライバー名',
    '備考',
  ] as const;

  // 案件データをCSV行に変換（型安全性向上）
  const rows: CsvRow[] = cases.map(caseData => {
    return [
      caseData.pickupDate || '', // 日付（YYYY-MM-DD形式）
      helpers.getBillingPartnerName(caseData.billingPartnerId), // 依頼元
      helpers.getPickupPartnerName(caseData.pickupPartnerId), // 引取地
      helpers.getDeliveryPartnerName(caseData.deliveryPartnerId), // 納車地
      helpers.getVehicleName(caseData.vehicleModelId), // 車名
      caseData.chassisNumber || '', // 車台番号
      caseData.price ?? 0, // 料金（nullの場合は0）
      helpers.getTransporterName(caseData.assignedLandTransporterId), // ドライバー名
      caseData.remarks || '', // 備考
    ];
  });

  // ファイル名生成（案件一覧_YYYYMMDD_HHmmss.csv）
  const filename = generateCsvFileName();

  return {
    headers: [...headers],
    rows,
    filename,
  };
};

/**
 * CSV出力用のファイル名を生成する
 *
 * 形式: 案件一覧_YYYYMMDD_HHmmss.csv
 *
 * @returns 生成されたファイル名
 *
 * @internal
 */
const generateCsvFileName = (): string => {
  const now = new Date();

  // YYYYMMDD形式の日付文字列
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const dateStr = `${year}${month}${day}`;

  // HHmmss形式の時刻文字列
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const timeStr = `${hours}${minutes}${seconds}`;

  return `案件一覧_${dateStr}_${timeStr}.csv`;
};
